import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import {
  getApiV1WeddingsByWeddingIdAuthMeOptions,
  getApiV1WeddingsByWeddingIdGuestsByGuestIdOptions,
  getApiV1WeddingsByWeddingIdGuestsOptions,
  postApiV1WeddingsByWeddingIdCheckinsMutation,
} from '@wedding-tech/api-client/tanstack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { useSettings } from '@/hooks/useSettings'
import { useAppForm } from '@/lib/forms/hook'
import { useLIFF } from '@/lib/liff/context'

import { ConfirmStep } from './checkin/ConfirmStep'
import { ManualStep } from './checkin/ManualStep'
import { SearchStep } from './checkin/SearchStep'
import { ThankYouStep } from './checkin/ThankYouStep'
import type { GuestItem, StepType } from './checkin/types'

// ─── Manual form schema ──────────────────────────────────────────────────────

const manualCheckinSchema = z.object({
  fullName: z.string().min(1, 'กรุณากรอกชื่อ-นามสกุล หรือชื่อเล่น'),
  displayName: z.string().min(1, 'กรุณากรอกชื่อที่ต้องการให้แสดงในงาน'),
  followers: z.string().min(1),
})

type ManualCheckinFormValues = z.infer<typeof manualCheckinSchema>

// ─── Main component ───────────────────────────────────────────────────────────

interface CheckinPageProps {
  checkinId?: string
}

export function CheckinPage({ checkinId }: CheckinPageProps) {
  const params = useParams({ strict: false }) as { eventId?: string }
  const weddingId = params.eventId ?? ''
  const { profile } = useLIFF()
  const queryClient = useQueryClient()

  const { data: userMe, isLoading: isMeLoading } = useQuery(
    getApiV1WeddingsByWeddingIdAuthMeOptions({ path: { weddingId } }),
  )
  const { isCheckInOpen, isLoading: isSettingsLoading } = useSettings()

  const { mutate: checkinMutate, isPending: isCheckinPending } = useMutation(
    postApiV1WeddingsByWeddingIdCheckinsMutation(),
  )

  // Fetch scanned guest data if checkinId (QR scan) is provided
  const { data: scannedGuestData, isLoading: isScannedGuestLoading } = useQuery(
    {
      ...getApiV1WeddingsByWeddingIdGuestsByGuestIdOptions({
        path: { weddingId, guestId: checkinId || '' },
      }),
      enabled: !!checkinId,
    },
  )

  // Fetch all guests list for search step
  const { data: allGuestsData, isFetching: isAllGuestsFetching } = useQuery({
    ...getApiV1WeddingsByWeddingIdGuestsOptions({
      path: { weddingId },
      query: { limit: 1000, page: 1 },
    }),
    enabled: !userMe?.status?.hasCheckedIn,
  })

  const allGuests = useMemo(
    () => (allGuestsData?.data ?? []) as GuestItem[],
    [allGuestsData],
  )

  const [step, setStep] = useState<StepType>('search')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGuest, setSelectedGuest] = useState<GuestItem | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const hasTriggeredAutoCheckin = useRef(false)

  // ── Manual Walk-in form ────────────────────────────────────────────────────
  const manualForm = useAppForm({
    defaultValues: {
      fullName: '',
      displayName: profile?.displayName || '',
      followers: '1',
    } as any,
    validators: { onChange: manualCheckinSchema },
    onSubmit: async ({ value }: { value: ManualCheckinFormValues }) => {
      if (!isCheckInOpen) {
        setSubmitError('ขออภัย ระบบเช็กอินปิดอยู่ในขณะนี้')
        return
      }
      setSubmitError('')
      checkinMutate(
        {
          path: { weddingId },
          body: {
            fullName: value.fullName.trim(),
            displayName: value.displayName.trim(),
            followers: Number(value.followers) || 1,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: getApiV1WeddingsByWeddingIdAuthMeOptions({
                path: { weddingId },
              }).queryKey,
            })
            setIsSubmitted(true)
          },
          onError: (err: any) => {
            setSubmitError(
              err.body?.error || err.message || 'เกิดข้อผิดพลาดในการเช็กอิน',
            )
          },
        },
      )
    },
  })

  // ── Confirmation step form ─────────────────────────────────────────────────
  const confirmForm = useAppForm({
    defaultValues: {
      displayName: '',
    },
    validators: {
      onChange: z.object({
        displayName: z.string().min(1, 'กรุณากรอกชื่อที่ต้องการให้แสดง'),
      }),
    },
    onSubmit: async ({ value }) => {
      if (!isCheckInOpen) {
        setSubmitError('ขออภัย ระบบเช็กอินปิดอยู่ในขณะนี้')
        return
      }
      if (!selectedGuest) return

      setSubmitError('')
      checkinMutate(
        {
          path: { weddingId },
          body: {
            guestId: selectedGuest.id,
            displayName: value.displayName.trim(),
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: getApiV1WeddingsByWeddingIdAuthMeOptions({
                path: { weddingId },
              }).queryKey,
            })
            setIsSubmitted(true)
          },
          onError: (err: any) => {
            setSubmitError(
              err.body?.error || err.message || 'เกิดข้อผิดพลาดในการเช็กอิน',
            )
          },
        },
      )
    },
  })

  // ── Auto Check-in for Linked Guests or Scanned QR ─────────────────────────
  useEffect(() => {
    if (
      isMeLoading ||
      isSettingsLoading ||
      isCheckinPending ||
      isSubmitted ||
      hasTriggeredAutoCheckin.current
    ) {
      return
    }

    // If already checked in according to backend, mark submitted
    if (userMe?.status?.hasCheckedIn) {
      return
    }

    // Scanned Guest via QR checkinId parameter
    if (checkinId && scannedGuestData) {
      const sg = scannedGuestData as any
      const count = sg.checkedInCount || 0
      const capacity = sg.expectedFollowers || 1
      const isFullyCheckedIn = sg.status === 'checked-in' && count >= capacity

      if (isFullyCheckedIn) {
        return
      }

      setSelectedGuest({
        id: sg.id || '',
        fullName: sg.fullName || '',
        displayName: sg.displayName || null,
        group: sg.group || undefined,
        expectedFollowers: sg.expectedFollowers ?? 1,
        checkedInCount: sg.checkedInCount ?? 0,
        status: sg.status || 'pending',
        tableAssignment: sg.tableAssignment,
      })
      confirmForm.setFieldValue(
        'displayName',
        sg.displayName || profile?.displayName || sg.fullName || '',
      )
      setStep('confirm')
      return
    }

    // User is linked to a guest record via LINE account (`preloadedGuest` from /auth/me)
    const linkedGuest = userMe?.preloadedGuest as any
    if (linkedGuest?.id && !userMe?.status?.hasCheckedIn) {
      hasTriggeredAutoCheckin.current = true
      setSubmitError('')

      checkinMutate(
        {
          path: { weddingId },
          body: {
            guestId: linkedGuest.id,
            displayName:
              linkedGuest.displayName ||
              profile?.displayName ||
              linkedGuest.fullName,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: getApiV1WeddingsByWeddingIdAuthMeOptions({
                path: { weddingId },
              }).queryKey,
            })
            setIsSubmitted(true)
          },
          onError: (err: any) => {
            const errorMsg =
              err.body?.error ||
              err.message ||
              'เกิดข้อผิดพลาดในการเช็กอินอัตโนมัติ'
            setSubmitError(errorMsg)
            // Fallback to confirm step so user can review and retry
            setSelectedGuest({
              id: linkedGuest.id,
              fullName: linkedGuest.fullName,
              displayName: linkedGuest.displayName,
              group: linkedGuest.group,
              expectedFollowers: linkedGuest.expectedFollowers ?? 1,
              checkedInCount: linkedGuest.checkedInCount ?? 0,
              status: linkedGuest.status || 'pending',
            })
            confirmForm.setFieldValue(
              'displayName',
              linkedGuest.displayName ||
                profile?.displayName ||
                linkedGuest.fullName,
            )
            setStep('confirm')
          },
        },
      )
    }
  }, [
    userMe,
    checkinId,
    scannedGuestData,
    isMeLoading,
    isSettingsLoading,
    isCheckinPending,
    isSubmitted,
    profile,
    weddingId,
    checkinMutate,
    confirmForm,
    queryClient,
  ])

  // ── Loading state ─────────────────────────────────────────────────────────
  if (
    isMeLoading ||
    isSettingsLoading ||
    isScannedGuestLoading ||
    (isCheckinPending && !selectedGuest && !submitError)
  ) {
    return <FullPageSpinnerPortal />
  }

  // ── Already checked-in / Thank You screen ──────────────────────────────────
  if (userMe?.status?.hasCheckedIn || isSubmitted) {
    const profileName =
      userMe?.profile?.displayName ||
      userMe?.profile?.fullName ||
      selectedGuest?.displayName ||
      selectedGuest?.fullName ||
      scannedGuestData?.displayName ||
      scannedGuestData?.fullName ||
      profile?.displayName ||
      'ผู้มีเกียรติ'
    const profileData = { fullName: profileName }

    const checkInDetail =
      userMe?.checkInDetail ||
      (scannedGuestData?.tableAssignment
        ? { table: scannedGuestData.tableAssignment.tableNumber }
        : null) ||
      (selectedGuest?.tableAssignment
        ? { table: selectedGuest.tableAssignment.tableNumber }
        : null)

    return (
      <ThankYouStep
        profileData={profileData}
        pictureUrl={profile?.pictureUrl || userMe?.profile?.pictureUrl}
        checkInDetail={checkInDetail}
      />
    )
  }

  // ── STEP: Search ───────────────────────────────────────────────────────────
  if (step === 'search') {
    return (
      <SearchStep
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        guests={allGuests}
        isFetching={isAllGuestsFetching}
        onSelectGuest={(guest) => {
          setSelectedGuest(guest)
          setSubmitError('')
          confirmForm.reset()
          confirmForm.setFieldValue(
            'displayName',
            guest.displayName || profile?.displayName || guest.fullName,
          )
          setStep('confirm')
        }}
        onManualRegister={() => {
          setSubmitError('')
          manualForm.reset()
          if (profile?.displayName) {
            manualForm.setFieldValue('displayName', profile.displayName)
            manualForm.setFieldValue('fullName', profile.displayName)
          }
          setStep('manual')
        }}
      />
    )
  }

  // ── STEP: Confirm ──────────────────────────────────────────────────────────
  if (step === 'confirm' && selectedGuest) {
    return (
      <ConfirmStep
        selectedGuest={selectedGuest}
        onBack={() => {
          setSelectedGuest(null)
          setSubmitError('')
          setStep('search')
        }}
        confirmForm={confirmForm}
        submitError={submitError}
        isCheckinPending={isCheckinPending}
      />
    )
  }

  // ── STEP: Manual Walk-in Form ──────────────────────────────────────────────
  return (
    <ManualStep
      onBack={() => {
        setSubmitError('')
        setStep('search')
      }}
      form={manualForm}
      submitError={submitError}
      isCheckinPending={isCheckinPending}
    />
  )
}
